import { Menu } from '@mantine/core';
import { Filter, Operator, SearchRequest } from '@medplum/core';
import { SearchParameter } from '@medplum/fhirtypes';
import {
  IconBleach,
  IconBleachOff,
  IconBracketsContain,
  IconBucket,
  IconBucketOff,
  IconCalendar,
  IconEqual,
  IconEqualNot,
  IconMathGreater,
  IconMathLower,
  IconSettings,
  IconSortAscending,
  IconSortDescending,
  IconX,
} from '@tabler/icons-react';
import {
  addLastMonthFilter,
  addMissingFilter,
  addNext24HoursFilter,
  addNextMonthFilter,
  addThisMonthFilter,
  addTodayFilter,
  addTomorrowFilter,
  addYearToDateFilter,
  addYesterdayFilter,
  buildFieldNameString,
  clearFiltersOnField,
  setSort,
} from '../SearchControl/SearchUtils';
import { useTranslation } from 'react-i18next';

export interface SearchPopupMenuProps {
  readonly search: SearchRequest;
  readonly searchParams?: SearchParameter[];
  readonly onPrompt: (searchParam: SearchParameter, filter: Filter) => void;
  readonly onChange: (definition: SearchRequest) => void;
}

export function SearchPopupMenu(
  props: SearchPopupMenuProps
): JSX.Element | null {
  if (!props.searchParams) {
    return null;
  }

  function onSort(searchParam: SearchParameter, desc: boolean): void {
    onChange(setSort(props.search, searchParam.code as string, desc));
  }

  function onClear(searchParam: SearchParameter): void {
    onChange(clearFiltersOnField(props.search, searchParam.code as string));
  }

  function onPrompt(searchParam: SearchParameter, operator: Operator): void {
    props.onPrompt(searchParam, {
      code: searchParam.code as string,
      operator,
      value: '',
    });
  }

  function onChange(definition: SearchRequest): void {
    props.onChange(definition);
  }

  // If there is only one search parameter, then show it directly
  if (props.searchParams.length === 1) {
    return (
      <SearchParameterSubMenu
        search={props.search}
        searchParam={props.searchParams[0]}
        onSort={onSort}
        onPrompt={onPrompt}
        onChange={onChange}
        onClear={onClear}
      />
    );
  }

  // Otherwise, show a menu, with each search parameter as a sub menu
  return (
    <Menu.Dropdown>
      {props.searchParams.map((searchParam) => (
        <Menu.Item key={searchParam.code}>
          {buildFieldNameString(searchParam.code as string)}
        </Menu.Item>
      ))}
    </Menu.Dropdown>
  );
}

interface SearchPopupSubMenuProps {
  readonly search: SearchRequest;
  readonly searchParam: SearchParameter;
  readonly onSort: (searchParam: SearchParameter, descending: boolean) => void;
  readonly onPrompt: (searchParam: SearchParameter, operator: Operator) => void;
  readonly onChange: (search: SearchRequest) => void;
  readonly onClear: (searchParam: SearchParameter) => void;
}

function SearchParameterSubMenu(props: SearchPopupSubMenuProps): JSX.Element {
  switch (props.searchParam.type) {
    case 'date':
      return <DateFilterSubMenu {...props} />;
    case 'number':
    case 'quantity':
      return <NumericFilterSubMenu {...props} />;
    case 'reference':
      return <ReferenceFilterSubMenu {...props} />;
    case 'string':
      return <TextFilterSubMenu {...props} />;
    case 'token':
    case 'uri':
      return <TokenFilterSubMenu {...props} />;
    default:
      return <>Unknown search param type: {props.searchParam.type}</>;
  }
}

function DateFilterSubMenu(props: SearchPopupSubMenuProps): JSX.Element {
  const { t } = useTranslation(['medplum-react']);

  const { searchParam } = props;
  const code = searchParam.code as string;
  return (
    <Menu.Dropdown>
      <Menu.Item
        leftSection={<IconSortAscending size={14} />}
        onClick={() => props.onSort(searchParam, false)}
      >
        {t('searchControl.submenus.date.asc', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconSortDescending size={14} />}
        onClick={() => props.onSort(searchParam, true)}
      >
        {t('searchControl.submenus.date.desc', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconEqual size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.EQUALS)}
      >
        {t('searchControl.submenus.common.eq', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconEqualNot size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.NOT_EQUALS)}
      >
        {t('searchControl.submenus.common.ne', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconMathLower size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.ENDS_BEFORE)}
      >
        {t('searchControl.submenus.date.lt', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconMathGreater size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.STARTS_AFTER)}
      >
        {t('searchControl.submenus.date.gt', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconBracketsContain size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.EQUALS)}
      >
        {t('searchControl.submenus.date.range', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconCalendar size={14} />}
        onClick={() => props.onChange(addTomorrowFilter(props.search, code))}
      >
        {t('searchControl.submenus.date.tomorrow', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconCalendar size={14} />}
        onClick={() => props.onChange(addTodayFilter(props.search, code))}
      >
        {t('searchControl.submenus.date.today', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconCalendar size={14} />}
        onClick={() => props.onChange(addYesterdayFilter(props.search, code))}
      >
        {t('searchControl.submenus.date.yesterday', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconCalendar size={14} />}
        onClick={() => props.onChange(addNext24HoursFilter(props.search, code))}
      >
        Next 24 Hours
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconCalendar size={14} />}
        onClick={() => props.onChange(addNextMonthFilter(props.search, code))}
      >
        {t('searchControl.submenus.date.nextMonth', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconCalendar size={14} />}
        onClick={() => props.onChange(addThisMonthFilter(props.search, code))}
      >
        {t('searchControl.submenus.date.thisMonth', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconCalendar size={14} />}
        onClick={() => props.onChange(addLastMonthFilter(props.search, code))}
      >
        {t('searchControl.submenus.date.lastMonth', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconCalendar size={14} />}
        onClick={() => props.onChange(addYearToDateFilter(props.search, code))}
      >
        {t('searchControl.submenus.date.oneYear', { ns: 'medplum-react' })}
      </Menu.Item>
      <CommonMenuItems {...props} />
    </Menu.Dropdown>
  );
}

function NumericFilterSubMenu(props: SearchPopupSubMenuProps): JSX.Element {
  const { t } = useTranslation(['medplum-react']);
  const { searchParam } = props;
  return (
    <Menu.Dropdown>
      <Menu.Item
        leftSection={<IconSortAscending size={14} />}
        onClick={() => props.onSort(searchParam, false)}
      >
        {t('searchControl.submenus.numeric.asc', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconSortDescending size={14} />}
        onClick={() => props.onSort(searchParam, true)}
      >
        {t('searchControl.submenus.numeric.desc', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconEqual size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.EQUALS)}
      >
        {t('searchControl.submenus.common.eq', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconEqualNot size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.NOT_EQUALS)}
      >
        {t('searchControl.submenus.common.ne', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconMathGreater size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.GREATER_THAN)}
      >
        {t('searchControl.submenus.numeric.gt', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconSettings size={14} />}
        onClick={() =>
          props.onPrompt(searchParam, Operator.GREATER_THAN_OR_EQUALS)
        }
      >
        {t('searchControl.submenus.numeric.ge', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconMathLower size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.LESS_THAN)}
      >
        {t('searchControl.submenus.numeric.lt', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconSettings size={14} />}
        onClick={() =>
          props.onPrompt(searchParam, Operator.LESS_THAN_OR_EQUALS)
        }
      >
        {t('searchControl.submenus.numeric.le', { ns: 'medplum-react' })}
      </Menu.Item>
      <CommonMenuItems {...props} />
    </Menu.Dropdown>
  );
}

function ReferenceFilterSubMenu(props: SearchPopupSubMenuProps): JSX.Element {
  const { t } = useTranslation(['medplum-react']);
  const { searchParam } = props;
  return (
    <Menu.Dropdown>
      <Menu.Item
        leftSection={<IconEqual size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.EQUALS)}
      >
        {t('searchControl.submenus.common.eq', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconEqualNot size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.NOT)}
      >
        {t('searchControl.submenus.common.ne', { ns: 'medplum-react' })}
      </Menu.Item>
      <CommonMenuItems {...props} />
    </Menu.Dropdown>
  );
}

function TextFilterSubMenu(props: SearchPopupSubMenuProps): JSX.Element {
  const { t } = useTranslation(['medplum-react']);
  const { searchParam } = props;
  return (
    <Menu.Dropdown>
      <Menu.Item
        leftSection={<IconSortAscending size={14} />}
        onClick={() => props.onSort(searchParam, false)}
      >
        {t('searchControl.submenus.text.asc', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconSortDescending size={14} />}
        onClick={() => props.onSort(searchParam, true)}
      >
        {t('searchControl.submenus.text.desc', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconEqual size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.EQUALS)}
      >
        {t('searchControl.submenus.common.eq', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconEqualNot size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.NOT)}
      >
        {t('searchControl.submenus.common.ne', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconBucket size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.CONTAINS)}
      >
        {t('searchControl.submenus.text.contains', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconBucketOff size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.EQUALS)}
      >
        {t('searchControl.submenus.text.notContains', { ns: 'medplum-react' })}
      </Menu.Item>
      <CommonMenuItems {...props} />
    </Menu.Dropdown>
  );
}

function TokenFilterSubMenu(props: SearchPopupSubMenuProps): JSX.Element {
  const { t } = useTranslation(['medplum-react']);
  const { searchParam } = props;
  return (
    <Menu.Dropdown>
      <Menu.Item
        leftSection={<IconEqual size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.EQUALS)}
      >
        {t('searchControl.submenus.common.eq', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconEqualNot size={14} />}
        onClick={() => props.onPrompt(searchParam, Operator.NOT)}
      >
        {t('searchControl.submenus.common.ne', { ns: 'medplum-react' })}
      </Menu.Item>
      <CommonMenuItems {...props} />
    </Menu.Dropdown>
  );
}

function CommonMenuItems(props: SearchPopupSubMenuProps): JSX.Element {
  const { t } = useTranslation(['medplum-react']);
  const { searchParam } = props;
  const code = searchParam.code as string;
  return (
    <>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconBleach size={14} />}
        onClick={() => props.onChange(addMissingFilter(props.search, code))}
      >
        {t('searchControl.submenus.common.missing', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Item
        leftSection={<IconBleachOff size={14} />}
        onClick={() =>
          props.onChange(addMissingFilter(props.search, code, false))
        }
      >
        {t('searchControl.submenus.common.present', { ns: 'medplum-react' })}
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        leftSection={<IconX size={14} />}
        onClick={() => props.onClear(searchParam)}
      >
        {t('searchControl.submenus.common.clearFilters', {
          ns: 'medplum-react',
        })}
      </Menu.Item>
    </>
  );
}
